import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './login.scss';
import { setJwtToken } from '../store/auth/authSlice';
import { useAppDispatch } from '../store/hooks';
import { useElevateGuestToUserMutation } from '../store/users/usersApi';

const audience = process.env.REACT_APP_AUTH0_AUDIENCE!;

type AuthLoginProps = {
	onLogin: () => void;
	onClose: () => void;
	title?: string;
	subtitle?: string;
	/** Add your own classes from the parent (e.g., your modal body). */
	className?: string;
};

export const AuthLogin = ({
	onLogin,
	onClose,
	title = 'Sign in to continue',
	subtitle = 'Choose a method below. You can sign up if you’re new.',
	className,
}: AuthLoginProps): React.JSX.Element => {
	const { loginWithRedirect, isLoading, loginWithPopup, getAccessTokenSilently } = useAuth0();
	const dispatch = useAppDispatch();
	const [elevateGuestToUser] = useElevateGuestToUserMutation();

	const handleAuth0LoginPopUp = async (connection: string): Promise<void> => {
		const authorizationParams = {
			audience,
			scope: 'openid profile email offline_access',
			connection,
		};

		try {
			await loginWithPopup({
				authorizationParams,
			});

			const token = await getAccessTokenSilently({
				authorizationParams: { audience, scope: 'openid profile email' },
			});

			dispatch(setJwtToken({ jwtToken: token }));

			await elevateGuestToUser({ jwtToken: token }).unwrap();

			onLogin();
		} catch (e: any) {
			const returnTo = typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '/';

			// Popup blocked or disabled? Fallback to redirect with same params.
			if (e?.error === 'popup_blocked' || e?.error === 'popup_not_allowed') {
				await loginWithRedirect({
					appState: { returnTo },
					authorizationParams,
				});

				return;
			}

			console.error('Popup login failed:', e);
		}
	};

	return (
		<div className={['auth-login', className].filter(Boolean).join(' ')}>
			<button type="button" onClick={onClose} aria-label="Close" className="auth-login__close">
				<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
					<path
						d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7a1 1 0 1 0-1.4 1.4L10.6 12l-4.9 4.9a1 1 0 1 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4Z"
						fill="currentColor"
					/>
				</svg>
			</button>

			<h2 className="auth-login__title">{title}</h2>
			<p className="auth-login__subtitle">{subtitle}</p>

			<div className="auth-login__actions">
				{/* Google */}
				<button
					type="button"
					disabled={isLoading}
					onClick={(): Promise<void> => handleAuth0LoginPopUp('google-oauth2')}
					className="auth-login__btn auth-login__btn--google"
				>
					<span className="auth-login__btnIcon" aria-hidden="true">
						{/* Google "G" icon (4-color) */}
						<svg width="18" height="18" viewBox="0 0 48 48">
							<path
								fill="#FFC107"
								d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.6-6.1 8-11.3 8-6.6 0-12-5.4-12-12S17.4 12 24 12c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.7 6 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.2-.4-3.5z"
							/>
							<path
								fill="#FF3D00"
								d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.7 6 29.1 4 24 4 16.1 4 9.2 8.5 6.3 14.7z"
							/>
							<path
								fill="#4CAF50"
								d="M24 44c5.9 0 10.9-2.3 14.6-6.1l-6.7-5.5C29.9 34.9 27.2 36 24 36c-5.1 0-9.4-3.3-11-7.9l-6.6 5.1C9.2 39.5 16.1 44 24 44z"
							/>
							<path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1 2.7-2.8 4.9-5.1 6.4l6.7 5.5C39.5 36.6 44 31 44 24c0-1.3-.1-2.2-.4-3.5z" />
						</svg>
					</span>
					<span className="auth-login__btnText">Continue with Google</span>
					{isLoading && <span className="auth-login__spinner" aria-hidden="true" />}
				</button>

				{/* Facebook */}
				<button
					type="button"
					disabled={isLoading}
					onClick={(): Promise<void> => handleAuth0LoginPopUp('facebook')}
					className="auth-login__btn auth-login__btn--facebook"
				>
					<span className="auth-login__btnIcon" aria-hidden="true">
						{/* Facebook "f" icon */}
						<svg width="18" height="18" viewBox="0 0 24 24">
							<path
								fill="currentColor"
								d="M22 12.07C22 6.48 17.52 2 12 2S2 6.48 2 12.07C2 17.1 5.66 21.25 10.44 22v-7.02H7.9v-2.91h2.54V9.41c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.23.2 2.23.2v2.46h-1.25c-1.23 0-1.61.77-1.61 1.56v1.87h2.74l-.44 2.91h-2.3V22C18.34 21.25 22 17.1 22 12.07Z"
							/>
						</svg>
					</span>
					<span className="auth-login__btnText">Continue with Facebook</span>
					{isLoading && <span className="auth-login__spinner" aria-hidden="true" />}
				</button>
			</div>

			<p className="auth-login__legal">
				By continuing, you agree to our <a href="/terms">Terms</a> and acknowledge our <a href="/privacy">Privacy Policy</a>.
			</p>
		</div>
	);
};
